/**
 * Catálogo de automatizaciones del repositorio `sereno_cron_jobs`
 * (Azure Function App `sereno-cron-jobs`, rg-sereno-shared-prod).
 *
 * Fuente de verdad: `function_app.py` y `app/services/*.py` de ese repo.
 * Al añadir, quitar o reprogramar un trigger allí, hay que actualizar este fichero
 * y la lista de interruptores del servidor (`server/src/services/automatizaciones.ts`).
 *
 * Los horarios son hora de Madrid: la Function App tiene WEBSITE_TIME_ZONE=Europe/Madrid
 * y el NCRONTAB se interpreta ya en local, sin convertir a UTC.
 */

export type AutomationCategory =
  | 'calendario'
  | 'huespedes'
  | 'accesos'
  | 'sincronizacion'
  | 'cobros'
  | 'operacion';

export interface AutomationParam {
  label: string;
  value: string;
  note?: string;
}

export interface Automation {
  /** Nombre de la función en function_app.py; es también el id de su interruptor */
  id: string;
  name: string;
  category: AutomationCategory;
  /** 'timer' = temporizador NCRONTAB; 'http' = endpoint bajo demanda */
  kind: 'timer' | 'http';
  /** Expresión NCRONTAB (6 campos) o método + ruta si es HTTP */
  cron?: string;
  endpoint?: string;
  /** Etiqueta corta para la píldora de la izquierda */
  when: string;
  /** Orden de aparición: minutos desde medianoche; semanales +2000; mensuales y anuales +2800; HTTP +5000 */
  sortKey: number;
  /** Una línea: qué hace */
  summary: string;
  /** Detalle: cómo lo hace y qué condiciones aplica */
  detail: string[];
  /** Fichero de servicio que contiene la lógica */
  service: string;
  /** Sistemas externos con los que habla */
  integrations: string[];
  /** Parámetros que gobiernan el comportamiento */
  params: AutomationParam[];
  /** Dónde avisa cuando hay algo que contar */
  notifies?: string;
  /** Cómo dispararla a mano, si se puede */
  manualRun?: string;
  /** Qué deja de pasar si se apaga desde el CRM: se enseña al confirmar el apagado */
  whenOff?: string;
  /** Si no tiene interruptor, por qué */
  noSwitchReason?: string;
}

export const CATEGORY_LABELS: Record<AutomationCategory, string> = {
  calendario: 'Calendario y precios',
  huespedes: 'Huéspedes',
  accesos: 'Accesos (Nuki)',
  sincronizacion: 'Sincronización',
  cobros: 'Cobros',
  operacion: 'Operación',
};

export const CATEGORY_STYLES: Record<AutomationCategory, { chip: string; dot: string; pill: string }> = {
  calendario: { chip: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500', pill: 'bg-blue-600' },
  huespedes: { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', pill: 'bg-emerald-600' },
  accesos: { chip: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', pill: 'bg-amber-600' },
  sincronizacion: { chip: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500', pill: 'bg-violet-600' },
  cobros: { chip: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', pill: 'bg-rose-600' },
  operacion: { chip: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-500', pill: 'bg-slate-600' },
};

/**
 * Fecha en la que se revisó este catálogo contra el código del repositorio.
 * ⚠️ Actualízala (formato YYYY-MM-DD) cada vez que toques la lista: es lo único
 * que le dice a quien lee la página si lo que está viendo sigue vigente.
 */
export const LAST_REVIEWED = '2026-10-05';

/** Commit de sereno_cron_jobs contra el que se revisó el catálogo. */
export const LAST_REVIEWED_COMMIT = 'f8d65f3';

export function formatReviewDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];
  return `${day} de ${meses[month - 1]} de ${year}`;
}

export const INFRA = {
  functionApp: 'sereno-cron-jobs',
  resourceGroup: 'rg-sereno-shared-prod',
  runtime: 'Python 3.12 · Consumption (Y1) · West Europe',
  url: 'https://sereno-cron-jobs.azurewebsites.net',
  timezone: 'Europe/Madrid (WEBSITE_TIME_ZONE)',
  keyVault: 'sereno-kv-f6v3uq5jr56gi (Managed Identity)',
  cosmos: 'sereno-main-db-f6v3uq5jr56gi',
  repo: 'sereno_cron_jobs',
  switches: 'sereno_shared/automation_switches',
  alerts: 'App Insights → topic Sistemas + correo',
};

export const AUTOMATIONS: Automation[] = [
  // ── Cada hora ────────────────────────────────────────────
  {
    id: 'reviews_sync_trigger',
    name: 'Sync de reviews + aviso',
    category: 'sincronizacion',
    kind: 'timer',
    cron: '0 0 * * * *',
    when: 'Cada hora',
    sortKey: 1,
    summary: 'Trae las reviews nuevas de Hostify, las traduce y las anuncia por Telegram.',
    detail: [
      'Lee el mapa de listings de Cosmos, descarga las reviews creadas en los últimos 2 días y guarda las nuevas en sereno_shared.hostify_reviews.',
      'Traduce con Azure Translator las que no vienen en español.',
      'Envía una notificación por cada review nueva, con el mensaje adaptado a la plataforma de origen y la nota reescalada (Airbnb y Vrbo sobre 5, Booking sobre 10).',
      'Las reviews de las propiedades de Alicante van a su propio grupo de Telegram.',
      'Lo que deja en Cosmos es lo que lee Analytics > Reseñas del CRM: si este trigger se para, esa sección se queda congelada en la última reseña sincronizada.',
      'Es además el latido de la vigilancia: la alerta «sin ejecuciones» salta si en 3 horas no se invoca ninguna función, y esta es la única que corre cada hora. Apagarla no la dispara: una función apagada se sigue invocando, solo que sale sin hacer nada.',
    ],
    service: 'app/services/reviews_sync_service.py',
    integrations: ['Hostify', 'Cosmos DB', 'Azure Translator', 'Telegram'],
    params: [
      { label: 'Ventana', value: 'reviews creadas en los últimos 2 días' },
      { label: 'Destino', value: 'sereno_shared.hostify_reviews' },
      { label: 'Escalas de nota', value: 'airbnb 5 · booking 10 · vrbo 5' },
      { label: 'Traductor', value: 'AZURE_TRANSLATOR_REGION = westeurope' },
      { label: 'Grupo alternativo', value: 'telegram-group-id-alc para Alicante' },
    ],
    notifies: 'Telegram · topic Reviews (y grupo de Alicante)',
    manualRun: 'scripts/send_recent_reviews_telegram.py · scripts/force_resend_review.py',
    whenOff: 'Dejan de llegar los avisos de reseñas nuevas y Analytics > Reseñas se queda congelada en la última sincronizada.',
  },

  // ── Cada seis horas ──────────────────────────────────────
  {
    id: 'fotos_web_trigger',
    name: 'Fotos de tusereno.es',
    category: 'sincronizacion',
    kind: 'timer',
    cron: '0 45 */6 * * *',
    when: 'Cada 6 h (:45)',
    sortKey: 2,
    summary: 'Prepara en WebP las fotos de la web que falten, para que tusereno.es las sirva ya hechas.',
    detail: [
      'Corre a las 00:45, 06:45, 12:45 y 18:45.',
      'Cada foto de las casas (Hostify) y cada foto propia de la web (contenedor assets) se guarda en WebP, en los anchos que usa la web, en el contenedor fotos de serenoassets43030a: hostify/<id>-<ancho>.webp y assets/<nombre>-<ancho>.webp.',
      'Los anchos y la calidad los decide la web: se leen de tusereno.es/fotos.json en cada vuelta y nunca se copian aquí.',
      'Solo hace las que faltan: una casa nueva o una foto cambiada en Hostify. Hasta entonces la web la prepara al vuelo, así que un retraso la hace más lenta, no la rompe.',
      'Tope de 6 minutos por vuelta (el plan corta a los 10): si un día faltan muchas —por ejemplo, al cambiar un ancho— se completan en varias vueltas.',
      'El fallo de una foto suelta queda en warning; solo aborta la vuelta entera si no hay Hostify o almacenamiento.',
    ],
    service: 'app/services/fotos_web_service.py',
    integrations: ['Hostify', 'Azure Storage', 'tusereno.es'],
    params: [
      { label: 'Destino', value: 'serenoassets43030a/fotos' },
      { label: 'Anchos y calidad', value: 'tusereno.es/fotos.json', note: 'los decide la web' },
      { label: 'Tope por vuelta', value: 'PRESUPUESTO_S = 360' },
      { label: 'Concurrencia', value: 'CONCURRENCIA = 3' },
      { label: 'Permiso', value: 'Storage Blob Data Contributor sobre serenoassets43030a' },
    ],
    whenOff: 'Las fotos nuevas o cambiadas no se preparan: la web las sigue enseñando, pero las genera al vuelo y va más lenta.',
  },

  // ── 02:00 ────────────────────────────────────────────────
  {
    id: 'nuki_employee_keys_trigger',
    name: 'Llaves Nuki de empleadas',
    category: 'accesos',
    kind: 'timer',
    cron: '0 0 2 * * *',
    when: 'Diario 02:00',
    sortKey: 120,
    summary: 'Sincroniza los códigos Nuki de las empleadas en todos los dispositivos según su estado en ficha.',
    detail: [
      'Lee las fichas de sereno_lavanderia/employees y aplica el estado deseado en cada cerradura: De Alta → llave creada y habilitada; De Baja → llave deshabilitada; Archivada → llave borrada.',
      'Nunca se fía del 200 OK asíncrono de Nuki: tras cada cambio espera y relee el dispositivo para confirmar el estado real.',
      'El estado real se guarda por dispositivo en nuki_sync.devices{} de la ficha. Los códigos rotados o archivados van al container nuki_code_history con deleted_from_devices[], para no volver a atacar dispositivos ya limpiados.',
      'Bucle device-centric con throttling para no chocar con el rate limit (429). Los dispositivos offline se saltan y se reintentan al día siguiente.',
      'El nuki_code lo genera otra aplicación; este cron solo sincroniza.',
    ],
    service: 'app/services/nuki_employee_keys_service.py',
    integrations: ['Nuki', 'Cosmos DB', 'Telegram'],
    params: [
      { label: 'Estados de ficha', value: 'De Alta / De Baja / Archivada' },
      { label: 'Nombre de la llave', value: '"{nombre} Servicio"' },
      { label: 'Tipo de llave', value: 'KEY_TYPE_KEYPAD = 13', note: 'código de teclado (PIN)' },
      { label: 'Verificación', value: 'VERIFY_DELAY_SECONDS = 6' },
      { label: 'Throttling', value: 'THROTTLE_SECONDS = 0.3' },
      { label: 'Reintentos', value: 'MAX_ATTEMPTS = 3' },
    ],
    notifies: 'Telegram · topic Nuki',
    whenOff: 'Las altas, bajas y archivados de empleadas dejan de llegar a las cerraduras: una empleada dada de baja seguiría abriendo.',
  },

  // ── 03:00 ────────────────────────────────────────────────
  {
    id: 'nuki_key_manager_trigger',
    name: 'Gestor de llaves offline Nuki',
    category: 'accesos',
    kind: 'timer',
    cron: '0 0 3 * * *',
    when: 'Diario 03:00',
    sortKey: 180,
    summary: 'Mantiene 5 llaves offline por dispositivo, borra las caducadas y las de la lista negra.',
    detail: [
      'Garantiza que cada cerradura tenga siempre un colchón de llaves offline libres para usarlas como respaldo cuando la creación de una llave de reserva falla.',
      'Elimina llaves caducadas y los códigos incluidos en la lista negra fija.',
      'No crea ni modifica códigos de empleadas: eso lo hace la automatización de las 02:00.',
    ],
    service: 'app/services/nuki_key_manager_service.py',
    integrations: ['Nuki', 'Cosmos DB'],
    params: [
      { label: 'Colchón offline', value: 'MIN_OFFLINE_KEYS = 5', note: 'por dispositivo' },
      { label: 'Lista negra', value: 'KEYS_TO_DELETE (5 códigos fijos)' },
      { label: 'Liberación', value: 'container used_offline_keys', note: 'libera los códigos tras el checkout' },
    ],
    whenOff: 'El colchón de llaves offline no se repone y las llaves caducadas se quedan en las cerraduras: la revisión de las 10:00 se puede quedar sin códigos de respaldo.',
  },

  // ── 04:00 ────────────────────────────────────────────────
  {
    id: 'calendar_block_trigger',
    name: 'Bloqueo de calendario same-day',
    category: 'calendario',
    kind: 'timer',
    cron: '0 0 4 * * *',
    when: 'Diario 04:00',
    sortKey: 240,
    summary: 'Bloquea el día de hoy y desbloquea el de ayer en el calendario de todas las propiedades.',
    detail: [
      'Impide reservas para el mismo día en Hostify: cada madrugada cierra la fecha de hoy y reabre la de ayer, de modo que la ventana de bloqueo se va desplazando sola.',
      'Recorre todas las propiedades del catálogo de Cosmos (hostify_listings).',
      'Sustituye a la antigua lambda de AWS hostify_workaround_180days_calendar.',
    ],
    service: 'app/services/calendar_block_service.py',
    integrations: ['Hostify', 'Cosmos DB'],
    params: [
      { label: 'Nota del bloqueo', value: '"Bloqueo 12h."' },
      { label: 'Zona horaria', value: 'Europe/Madrid' },
    ],
    whenOff: 'Hostify vuelve a aceptar reservas para el mismo día.',
  },

  // ── 04:30 ────────────────────────────────────────────────
  {
    id: 'calendar_guard_trigger',
    name: 'Guard del año siguiente',
    category: 'calendario',
    kind: 'timer',
    cron: '0 30 4 * * *',
    when: 'Diario 04:30',
    sortKey: 270,
    summary: 'Cierra la disponibilidad del año siguiente que se haya abierto por error.',
    detail: [
      'De enero a noviembre la última fecha que puede estar abierta es el 31/12 del año en curso; en diciembre el límite pasa al 31/12 del año siguiente. El guard nunca reabre: la apertura del año siguiente se hace a mano.',
      'Las propiedades se leen de Hostify, no de Cosmos: una propiedad recién creada aún no está en hostify_listings (ese sync corre a las 05:00) y son justo esas las que aparecen con el año siguiente abierto.',
      'Solo toca los días available. Los booked y los ya unavailable no se tocan nunca. Ojo: status: "booked" no implica reserva — los bloqueos manuales también salen así; el único indicio fiable es reservation_id, contrastado con GET /reservations.',
      'Si hay una reserva real dentro de la ventana vigilada, avisa por Telegram, respeta la reserva con un día de margen a cada lado y cierra el resto de días abiertos de esa propiedad. El aviso se deduplica en Cosmos (calendar_guard) para no repetirlo cada noche.',
      'Rendimiento: per_page=400 devuelve el año entero en una llamada (~0,7 s) y la flota se barre en ~40 s. Ante timeouts hace bisección adaptativa de la ventana de lectura.',
      'Tras cerrar relee el calendario en vez de fiarse del 200 OK, y avisa de los días que sigan abiertos.',
    ],
    service: 'app/services/calendar_guard_service.py',
    integrations: ['Hostify', 'Cosmos DB', 'Telegram'],
    params: [
      { label: 'Exentas', value: 'CALENDAR_GUARD_EXEMPT = ALC1,ALC2,ALC3,ALC4', note: 'subcadena del nickname' },
      { label: 'Modo informe', value: 'CALENDAR_GUARD_REPORT_ONLY = false', note: 'true → informa sin cerrar' },
      { label: 'Margen de reserva', value: 'RESERVATION_MARGIN_DAYS = 1' },
      { label: 'Escritura', value: 'MAX_WRITE_SPAN_DAYS = 62', note: 'tramos contiguos por PUT' },
      { label: 'Lectura', value: 'CALENDAR_PER_PAGE = 400 · mínimo 15 días por bisección' },
      { label: 'Pausa entre llamadas', value: 'PAUSE_BETWEEN_CALLS_S = 0.2' },
    ],
    notifies: 'Telegram · topic Reservas',
    manualRun: 'POST /api/calendar-guard?listing=<texto>&dry_run=1 (function key) · en local, scripts/test_calendar_guard.py',
    whenOff: 'Si alguien abre por error el año siguiente en una propiedad, nadie lo cierra ni avisa.',
  },

  // ── 05:00 ────────────────────────────────────────────────
  {
    id: 'listings_sync_trigger',
    name: 'Sync de propiedades Hostify → Cosmos',
    category: 'sincronizacion',
    kind: 'timer',
    cron: '0 0 5 * * *',
    when: 'Diario 05:00',
    sortKey: 300,
    summary: 'Replica el catálogo de propiedades activas de Hostify en CosmosDB.',
    detail: [
      'Descarga las listings activas (service_pms=1) de Hostify, y para cada una sus children (IDs de Booking, Vrbo y Airbnb) y los custom fields relevantes.',
      'Hace upsert en sereno_shared.hostify_listings y desactiva las listings que ya no están en Hostify.',
      'Es la fuente de la que beben el resto de automatizaciones y apps (housekeeping, lavandería, portal).',
      'Limpia los tags HTML que Hostify inyecta a veces en campos de texto libre (ej. el código del porterillo llega como <p>0000</p>).',
    ],
    service: 'app/services/listings_sync_service.py',
    integrations: ['Hostify', 'Cosmos DB'],
    params: [
      { label: 'Destino', value: 'sereno_shared.hostify_listings' },
      { label: 'Ritmo', value: 'REQUEST_DELAY_S = 0.2', note: '200 ms entre llamadas' },
      {
        label: 'Custom fields copiados',
        value: '1236 smartlock · 1840 smartlock2 · 2503 opener · 2504 switchbot · 1159 cajetín · 1160 cafetera · 1164 garaje · 1158/1149 wifi · 1249 licencia · 1535 porterillo',
      },
    ],
    whenOff: 'Las propiedades nuevas o cambiadas en Hostify no llegan a Cosmos, y de ese catálogo beben housekeeping, lavandería, el portal y el resto de automatizaciones.',
  },

  // ── Lunes 06:00 ──────────────────────────────────────────
  {
    id: 'nuki_posture_trigger',
    name: 'Postura de seguridad Nuki',
    category: 'accesos',
    kind: 'timer',
    cron: '0 0 6 * * 1',
    when: 'Lunes 06:00',
    sortKey: 2360,
    summary: 'Verifica y corrige la configuración de seguridad de todas las cerraduras y openers.',
    detail: [
      'Postura deseada: alertas de batería por email, emparejamiento Bluetooth desactivado, auto-updates activados y botones de los openers desactivados.',
      'Flujo: lee el estado completo de cada dispositivo, lo compara con la postura, relee justo antes de escribir, aplica las correcciones por bloque de config, espera y verifica releyendo.',
      'El POST de config de Nuki no es un parche: toma el body como objeto completo, así que hay que hacer read-modify-write del bloque entero y un solo POST por bloque con todos los campos a la vez (un POST por campo revierte los anteriores).',
      'El 204 solo confirma que Nuki aceptó la petición, no que el dispositivo la aplicara — de ahí la verificación diferida.',
      'Coordenadas a 0.0 dejan el bloque config en solo lectura: el servicio lo detecta antes de gastar la llamada y lo reporta. scripts/nuki_fill_missing_coordinates.py las rellena.',
      'Si más de 25 dispositivos salen no conformes, aborta sin escribir nada y avisa: es más probable un fallo sistémico que 25 incidencias reales.',
      'El informe de Telegram agrupa los fallos por motivo, no por dispositivo. Los offline no conformes van en su propia sección.',
    ],
    service: 'app/services/nuki_posture_service.py',
    integrations: ['Nuki', 'Telegram'],
    params: [
      { label: 'Tope de seguridad', value: 'MAX_DEVICES_TO_FIX = 25', note: 'por encima, aborta' },
      { label: 'Verificación', value: 'VERIFY_DELAY_SECONDS = 8' },
      { label: 'Reintentos', value: 'MAX_RETRIES = 1' },
      { label: 'Throttling', value: 'THROTTLE_SECONDS = 0.3' },
      { label: 'Informe', value: 'MAX_TELEGRAM_CHARS = 3800' },
    ],
    notifies: 'Telegram · topic Nuki',
    manualRun: 'scripts/test_nuki_posture.py (dry-run, --device para acotar, --apply para escribir) · scripts/diagnose_nuki_posture.py',
    whenOff: 'Nadie revisa ni corrige la configuración de seguridad de las cerraduras (Bluetooth, actualizaciones, botones de los openers).',
  },

  // ── 09:30 ────────────────────────────────────────────────
  {
    id: 'second_payments_trigger',
    name: 'Segundos pagos de la web',
    category: 'cobros',
    kind: 'timer',
    cron: '0 30 9 * * *',
    when: 'Diario 09:30',
    sortKey: 570,
    summary: 'Dispara el cobro del saldo pendiente de las reservas directas y de tusereno.es.',
    detail: [
      'El timer solo hace un POST autenticado a https://tusereno.es/api/cron/second-payments y notifica el resultado; la lógica de cobro vive en la web.',
      'La web recorre las reservas de los orígenes Tu Sereno, Website y Direct, calcula el saldo pendiente por tramos (50% del total a ≤15 días del check-in, 100% a ≤7 días), cobra off-session en Stripe con la tarjeta guardada del checkout y registra la transacción en Hostify.',
      'Existe porque los autopagos de Hostify no pueden usar el Stripe customer vinculado (confirmado por su soporte el 12/07/2026).',
      'La web responde en dry-run salvo que tenga SECOND_PAYMENTS_MODE=live; el modo sale en el propio aviso.',
      'Solo avisa por Telegram si hubo movimiento: cobros, recuperaciones, fallos o exclusiones. Las excluidas por marca manual también se listan, para que quede constancia de que ese día no se cobró aposta y no parezca un fallo silencioso.',
      'Cada línea del aviso enlaza la reserva en Hostify, indica el canal cuando no es la web y si se avisó al huésped (aviso de reintento, o payment request + mensaje).',
    ],
    service: 'function_app.py (llamada HTTP) + tusereno.es /api/cron/second-payments',
    integrations: ['tusereno.es', 'Stripe', 'Hostify', 'Telegram'],
    params: [
      { label: 'Autenticación', value: 'Bearer Cron-Secret (Key Vault)' },
      { label: 'Orígenes', value: 'Tu Sereno · Website · Direct' },
      { label: 'Tramos de cobro', value: '50% a ≤15 días del check-in · 100% a ≤7 días' },
      { label: 'Modo', value: 'SECOND_PAYMENTS_MODE en la web', note: 'dry-run salvo live' },
      { label: 'Timeout', value: '120 s' },
    ],
    notifies: 'Telegram · topic Reservas (solo si hay cobros, fallos o exclusiones)',
    whenOff: 'No se cobra el segundo pago de las reservas directas y de la web: habrá que cobrarlo a mano mientras esté apagada.',
  },

  // ── 10:00 ────────────────────────────────────────────────
  {
    id: 'nuki_revision_keys_trigger',
    name: 'Revisión de llaves de reservas',
    category: 'accesos',
    kind: 'timer',
    cron: '0 0 10 * * *',
    when: 'Diario 10:00',
    sortKey: 600,
    summary: 'Comprueba que las reservas con check-in hoy o mañana tengan su llave creada en Nuki, y la pone si falta.',
    detail: [
      'Si la reserva ya tiene código (custom field 1166) pero no está en la cerradura: intenta crearlo en Nuki; si falla, le asigna un código offline libre del smartlock (llave con "offline" en el nombre) reescribiendo el 1166 en Hostify.',
      'Si la reserva no tiene código: sin smartlock asociado (1236 vacío) no aplica; con el smartlock online genera un código nuevo, lo crea en Nuki y lo escribe en el 1166; con el smartlock offline, o si la creación falla, asigna un código offline libre al azar y marca el 1341 = "Yes".',
      'Si no quedan códigos offline libres, alerta por Telegram sin solución aplicada — requiere intervención manual.',
      'Todo lo que hace lo notifica por Telegram: ninguna corrección es silenciosa.',
      'Los códigos offline asignados se registran en used_offline_keys para que el gestor de llaves de las 03:00 los libere tras el checkout.',
    ],
    service: 'app/services/nuki_revision_keys_service.py',
    integrations: ['Hostify', 'Nuki', 'Cosmos DB', 'Telegram'],
    params: [
      { label: 'Ventana', value: 'check-in hoy y mañana' },
      { label: 'Campo código de llave', value: 'custom_field 1166' },
      { label: 'Campo smartlock', value: 'custom_field 1236' },
      { label: 'Marca de offline usado', value: 'custom_field 1341 = "Yes"' },
      { label: 'Tipo de llave', value: 'KEY_TYPE_KEYPAD = 13' },
    ],
    notifies: 'Telegram · topic Nuki',
    whenOff: 'Nadie comprueba que las reservas de hoy y mañana tengan su llave en la cerradura: un huésped podría llegar con un código que no abre.',
  },

  {
    id: 'factura_azure_trigger',
    name: 'Aviso de la factura de Azure',
    category: 'operacion',
    kind: 'timer',
    cron: '0 0 10 * * *',
    when: 'Diario 10:00 (avisa el día de la factura)',
    sortKey: 605,
    summary: 'El día que Azure emite la factura del mes (el 9, de madrugada) avisa en Sistemas de su importe y de cuánto ha cambiado respecto a la anterior.',
    detail: [
      'Corre a diario y solo publica si Azure ha emitido una factura con importe en las últimas 24 horas. Así no guarda de cuáles ha avisado ya, y si Azure cambia el día de facturación sigue funcionando.',
      'El aviso lleva el total con y sin IVA, la diferencia con la factura anterior, si está cobrada y el número.',
      'No avisa de los documentos de 0 € que Azure emite cada mes hacia el día 2.',
      'Si un día no corre, esa factura no se avisa: el PDF llega igualmente por correo a contabilidad@tusereno.es.',
    ],
    service: 'app/services/factura_azure_service.py',
    integrations: ['Azure Billing', 'Telegram'],
    params: [
      { label: 'Perfil de facturación', value: 'GGAF-MZV5-BG7-PGB · Sereno Tech S.L.' },
      { label: 'Ventana', value: 'VENTANA_HORAS = 24' },
      { label: 'Permiso', value: 'rol de facturación Billing profile reader', note: 'identidad de la Function App sobre el perfil' },
    ],
    notifies: 'Telegram · topic Sistemas (solo el día que hay factura)',
    manualRun: 'scripts/test_factura_azure.py --horas 720 (muestra el aviso de la última factura sin enviarlo)',
    whenOff: 'No llega el aviso mensual del gasto de Azure a Sistemas. La factura sigue llegando por correo a contabilidad@tusereno.es.',
  },

  // ── 11:00 y 15:00 ────────────────────────────────────────
  {
    id: 'early_checkin_trigger',
    name: 'Early check-in automático',
    category: 'huespedes',
    kind: 'timer',
    cron: '0 0 11,15 * * *',
    when: 'Diario 11:00 y 15:00',
    sortKey: 660,
    summary: 'Ofrece entrada anticipada cuando la propiedad ya está limpia y hay reserva hoy.',
    detail: [
      'Escanea las propiedades marcadas como limpias con reserva de entrada hoy y verifica que las cerraduras Nuki estén online.',
      'Si se cumple, habilita el early check-in: actualiza la hora de llegada prevista (planned_arrival) en Hostify, avisa al huésped por el inbox de Hostify y por WhatsApp, marca el custom field 2386 y saca la propiedad de completed_cleanings.',
      'El WhatsApp usa la plantilla early_checkin_v2, de categoría Utility: la anterior era de Marketing y Meta tumbaba una parte de los envíos por el tope por usuario (error 131049). El texto tiene que coincidir literalmente con la plantilla aprobada.',
      'La misma lógica se dispara además bajo demanda desde la app de housekeeping en cuanto se confirma una limpieza (endpoint POST /api/early-checkin, con su propio interruptor).',
    ],
    service: 'app/services/early_checkin_service.py',
    integrations: ['Hostify', 'Nuki', 'Cosmos DB', 'WhatsApp', 'Telegram'],
    params: [
      { label: 'Campo de marca', value: 'custom_field 2386 (early_checkin)' },
      { label: 'Plantilla de WhatsApp', value: 'early_checkin_v2 (Utility)' },
      { label: 'Canales revisados', value: 'parent_id · booking_id · vrbo_id · airbnb_id' },
      { label: 'Condiciones', value: 'propiedad limpia + reserva hoy + Nuki online' },
    ],
    notifies: 'Telegram · topic Lavandería',
    whenOff: 'No se ofrece la entrada anticipada en los barridos de las 11:00 y las 15:00. La que lanza la app de housekeeping al confirmar una limpieza sigue funcionando: tiene su propio interruptor.',
  },

  // ── Lunes 12:00 ──────────────────────────────────────────
  {
    id: 'custom_field_validator_trigger',
    name: 'Validador de smartlock_id',
    category: 'accesos',
    kind: 'timer',
    cron: '0 0 12 * * 1',
    when: 'Lunes 12:00',
    sortKey: 2720,
    summary: 'Verifica que el ID de cerradura coincida entre listings parent y children en Hostify, y corrige los children.',
    detail: [
      'Compara custom_field_1236 (smartlock ID) entre cada listing parent y sus children de los distintos canales.',
      'Una discrepancia hace que una reserva de Booking o Airbnb apunte a la cerradura equivocada, así que la corrige sola: copia el valor del parent a los children afectados, con una llamada por grupo.',
      'Si el parent no tiene valor no toca nada y lo deja en el informe: no sabe cuál es el bueno.',
      'Informa por Telegram de las discrepancias, las corregidas y los errores.',
      'Sustituye a la antigua lambda de AWS hostify_custom_field_validator.',
    ],
    service: 'app/services/custom_field_validator_service.py',
    integrations: ['Hostify', 'Cosmos DB', 'Telegram'],
    params: [
      { label: 'Campo validado', value: 'custom_field 1236 (smartlock_id)' },
      { label: 'Corrección', value: 'valor del parent → children', note: 'se salta si el parent está vacío' },
    ],
    notifies: 'Telegram · topic Nuki',
    whenOff: 'Si un canal (Booking, Airbnb, Vrbo) apunta a otra cerradura que su propiedad, nadie lo detecta ni lo corrige.',
  },

  // ── 21:00 ────────────────────────────────────────────────
  {
    id: 'linen_reminder_trigger',
    name: 'Recordatorio de ropa de cama',
    category: 'huespedes',
    kind: 'timer',
    cron: '0 0 21 * * *',
    when: 'Diario 21:00',
    sortKey: 1260,
    summary: 'Avisa a las estancias largas de que pueden pedir cambio de sábanas y toallas.',
    detail: [
      'Recorre las reservas en curso (aceptadas, ya entradas y sin salir) de 8 días o más, y envía el recordatorio el día siguiente al punto medio de la estancia.',
      'El mensaje va al huésped por el inbox de Hostify (canal de la reserva y WhatsApp), y se notifica por Telegram.',
      'Marca el envío con el custom field 2387 = "Yes" de la reserva para no repetirlo.',
      'Excluye las propiedades de Alicante (prefijo AL) y las de traslado (TRF).',
    ],
    service: 'app/services/linen_reminder_service.py',
    integrations: ['Hostify', 'Cosmos DB', 'WhatsApp', 'Telegram'],
    params: [
      { label: 'Estancia mínima', value: 'MIN_RESERVATION_DAYS = 8' },
      { label: 'Momento del aviso', value: 'día siguiente al punto medio de la estancia' },
      { label: 'Marca de enviado', value: 'custom_field 2387 = "Yes"' },
      { label: 'Excluidas', value: 'prefijos AL y TRF' },
    ],
    notifies: 'Telegram · topic Lavandería',
    whenOff: 'Las estancias largas dejan de recibir el aviso de que pueden pedir cambio de sábanas y toallas.',
  },

  // ── 22:00 ────────────────────────────────────────────────
  {
    id: 'cleaning_review_trigger',
    name: 'Revisión nocturna de limpiezas',
    category: 'operacion',
    kind: 'timer',
    cron: '0 0 22 * * *',
    when: 'Diario 22:00',
    sortKey: 1320,
    summary: 'Desmarca como limpias las propiedades que en realidad tienen huésped alojado.',
    detail: [
      'Cada noche contrasta las propiedades marcadas como limpias (completed_cleanings) con las reservas en curso.',
      'Si hay alguien dentro, la desmarca: la borra de completed_cleanings y la pone como Dirty en Hostify, para que la app de housekeeping no la dé por disponible al día siguiente.',
      'Cubre los cuatro IDs de canal de cada propiedad (parent, Booking, Vrbo y Airbnb) para no dar por libre una propiedad ocupada por una reserva de OTA.',
      'Manda un resumen por Telegram con las propiedades afectadas.',
    ],
    service: 'app/services/cleaning_review_service.py',
    integrations: ['Hostify', 'Cosmos DB', 'Telegram'],
    params: [{ label: 'Canales revisados', value: 'parent_id · booking_id · vrbo_id · airbnb_id' }],
    notifies: 'Telegram · topic Lavandería',
    whenOff: 'Una propiedad ocupada podría seguir marcada como limpia en la app de housekeeping, y en Hostify.',
  },

  // ── Domingo 23:00 ────────────────────────────────────────
  {
    id: 'reservations_sync_trigger',
    name: 'Reconciliación semanal de reservas',
    category: 'sincronizacion',
    kind: 'timer',
    cron: '0 0 23 * * 0',
    when: 'Domingo 23:00',
    sortKey: 2700,
    summary: 'Contrasta las reservas de la última semana entre Hostify y CosmosDB, en los dos sentidos.',
    detail: [
      'Pasada forward (Hostify → Cosmos): descarga las reservas de la última semana y crea las que faltan o actualiza las que difieren en algún campo clave.',
      'Pasada reverse (Cosmos → Hostify): re-verifica una a una, con GET /reservations/{id}, las reservas que seguimos teniendo como aceptadas y que están en curso o son futuras. Esto cierra el agujero de la pasada forward, que pagina sobre un orden inestable (updated_at) y puede saltarse reservas — una cancelación que la lista no devuelva nunca se corregiría sin esta segunda pasada.',
      'No copia las reservas de prueba. Las pruebas internas (portal, web, CRM) crean reservas reales en Hostify, que no deja borrarlas: se quedan canceladas para siempre, y al copiarlas reaparecían en las analíticas y en los ingresos por propiedad. Se reconocen por el huésped (lista de cuentas de prueba) y por la nota con la que firman las sondas automáticas.',
      'Cada reserva se maneja de forma aislada: una que falle no aborta el resto.',
      'Devuelve un informe con totales y la lista de discrepancias corregidas.',
    ],
    service: 'app/services/reservations_sync_service.py',
    integrations: ['Hostify', 'Cosmos DB'],
    params: [
      { label: 'Ventana', value: 'LOOKBACK_DAYS = 7' },
      { label: 'Buffer pasada reverse', value: 'REVERSE_CHECKOUT_BUFFER_DAYS = 7' },
      { label: 'Campos comparados', value: 'status · paid_part · paid_sum · checkIn · checkOut · updated_at' },
      { label: 'Ritmo pasada reverse', value: 'REVERSE_REQUEST_DELAY_S = 0.15' },
      { label: 'Reservas de prueba', value: 'TEST_GUEST_IDS (16 cuentas) · nota "prueba autom" / "test autom"', note: 'para añadir una cuenta, se mete su guest_id' },
    ],
    whenOff: 'Las reservas o cancelaciones que se salte el webhook no se corrigen en Cosmos, y de ahí salen las analíticas y los ingresos por propiedad.',
  },

  // ── Día 1 del mes, 02:00 ─────────────────────────────────
  {
    id: 'guest_customers_sync_trigger',
    name: 'Reconciliación mensual de huéspedes y clientes',
    category: 'sincronizacion',
    kind: 'timer',
    cron: '0 0 2 1 * *',
    when: 'Día 1 del mes, 02:00',
    sortKey: 2800,
    summary: 'Repara las fichas de huésped y los clientes unificados que alimentan Clientes › Huéspedes.',
    detail: [
      'NO es el alimentador: el día a día lo cubren los webhooks de hostify-webhook-app, que en cada reserva escriben la ficha del huésped con su estancia anidada y rehacen el cliente al que pertenece.',
      'Repara los tres huecos que el tiempo real no puede cubrir: reservas que entraron por la reconciliación semanal y no por webhook (ese camino escribe hostify_reservations y no toca guests), clientes huérfanos cuando un huésped cambia de teléfono, y linkedGuestIds, que necesita la foto global.',
      'De paso refresca la ficha con lo que Hostify sepa hoy del huésped: nombre, teléfono, país, idioma y canal de origen.',
      'Colapsa las reservas duplicadas de Hostify con el mismo criterio que el CRM (mismo código de confirmación, alojamiento y fechas; se queda la de id más alto). Sin esto, la reconciliación desharía el arreglo, porque reconstruye desde hostify_reservations, donde los duplicados siguen estando.',
      'Los alojamientos que ya no gestionamos se preguntan a Hostify uno a uno: hostify_listings solo tiene los actuales, y sin ese respaldo ~1 de cada 8 estancias antiguas perdería el nombre de la casa.',
      'Va concurrente por obligación: en serie son ~40 minutos y el plan Consumption corta a los 10. Con 20 peticiones en paralelo la pasada completa tarda ~3 minutos.',
      'El barrido de clientes obsoletos solo se ejecuta si la pasada fue perfecta: un fallo de red puntual dejaría un cliente fuera del recuento y el barrido lo daría por obsoleto, borrando el documento que lee la centralita al descolgar.',
      'No toca el bloque de llamadas del cliente: lo escribe call-ingest y aquí se arrastra tal cual.',
    ],
    service: 'app/services/guest_customers_sync_service.py',
    integrations: ['Hostify', 'Cosmos DB'],
    params: [
      { label: 'Concurrencia', value: 'CONCURRENCIA = 20' },
      { label: 'Tope de estancias por ficha', value: 'MAX_STAYS = 300' },
      { label: 'Ritmo consultas de alojamiento', value: 'LISTING_LOOKUP_DELAY_S = 0.15' },
      { label: 'Contenedores', value: 'guests · guest_customers · hostify_reservations · hostify_listings' },
    ],
    whenOff: 'Clientes › Huéspedes deja de repararse: las reservas que no entraron por webhook se quedan sin ficha y los clientes huérfanos no se limpian.',
  },

  // ── 15 de septiembre, 09:00 ──────────────────────────────
  {
    id: 'pricelabs_sync_off_trigger',
    name: 'Fin de temporada en PriceLabs',
    category: 'calendario',
    kind: 'timer',
    cron: '0 0 9 15 9 *',
    when: '15 sept 09:00 (anual)',
    sortKey: 2900,
    summary: 'Cada 15 de septiembre apaga el precio dinámico de PriceLabs en todas las propiedades salvo las de Alicante.',
    detail: [
      'Con el sync apagado PriceLabs deja de mandar precios a Hostify y los que haya se quedan fijos. Las de Alicante (ALC…) lo conservan porque trabajan todo el año.',
      'Solo escribe las propiedades que tienen el sync encendido, y después relee el listado para confirmar el apagado en vez de fiarse del 200 OK. Si alguna sigue encendida, lo dice en el aviso y lanza la alerta de fallo.',
      'El aviso sale siempre, aunque no haya nada que apagar: es el recordatorio anual de que la temporada se ha cerrado.',
      'Volver a encender el sync es manual: PriceLabs pide revisar los precios en su web antes de reactivarlo.',
      'No depende de Cosmos: una tarea que corre una vez al año no puede perderse porque Cosmos esté caído.',
    ],
    service: 'app/services/pricelabs_sync_off_service.py',
    integrations: ['PriceLabs', 'Telegram'],
    params: [
      { label: 'Exentas', value: 'PRICELABS_SYNC_OFF_EXEMPT = ALC', note: 'subcadena del nombre en PriceLabs' },
      { label: 'API', value: 'Customer API · GET y POST /v1/listings' },
      { label: 'Campo', value: 'push_enabled = "false"', note: 'como texto, no booleano' },
    ],
    notifies: 'Telegram · topic Sistemas (siempre)',
    manualRun: 'scripts/test_pricelabs_sync_off.py (dry-run; --apply para hacerlo de verdad)',
    whenOff: 'El 15 de septiembre no se apaga el precio dinámico: PriceLabs seguiría mandando precios a Hostify fuera de temporada.',
  },

  // ── HTTP ─────────────────────────────────────────────────
  {
    id: 'early_checkin_http',
    name: 'Early check-in bajo demanda',
    category: 'huespedes',
    kind: 'http',
    endpoint: 'POST /api/early-checkin',
    when: 'HTTP',
    sortKey: 5010,
    summary: 'Evalúa el early check-in de una propiedad en cuanto la app de housekeeping confirma su limpieza.',
    detail: [
      'La app de housekeeping lo llama al cerrar una limpieza, sin esperar al barrido de las 11:00 y 15:00.',
      'Body JSON: parent_id (ID de la propiedad) y timestamp_str (momento de la limpieza, YYYY-MM-DD HH:MM:SS), más attempt y max_attempts.',
      'Si no hay reserva elegible, Nuki no está listo o la cerradura está offline responde 200 con {"status":"skipped"} — es un resultado esperado, no un error, y así no dispara alertas falsas.',
      'La app de housekeeping reintenta hasta 3 veces y avisa ella misma si los agota. Por eso, mientras quedan intentos, un fallo se registra en warning y solo el último dispara la alerta.',
    ],
    service: 'app/services/early_checkin_service.py',
    integrations: ['App de housekeeping', 'Hostify', 'Nuki', 'Cosmos DB'],
    params: [
      { label: 'Autenticación', value: 'function key (auth_level=FUNCTION)' },
      { label: 'Parámetros', value: 'parent_id · timestamp_str · attempt · max_attempts' },
      { label: 'Respuestas', value: '200 ok / 200 skipped / 200 disabled / 400 / 404 / 500' },
    ],
    whenOff: 'Al confirmar una limpieza ya no se ofrece la entrada anticipada al momento; la app de housekeeping recibe «disabled» y no reintenta. Los barridos de las 11:00 y 15:00 siguen, si están encendidos.',
  },
  {
    id: 'calendar_guard_http',
    name: 'Guard de calendario manual',
    category: 'calendario',
    kind: 'http',
    endpoint: 'POST /api/calendar-guard',
    when: 'HTTP',
    sortKey: 5020,
    summary: 'Ejecuta el guard de calendario a mano, con la opción de acotarlo a una propiedad o hacerlo en seco.',
    detail: [
      'Mismo servicio que el timer de las 04:30, pero disparado cuando hace falta — por ejemplo tras dar de alta una propiedad.',
      'Devuelve el informe completo en JSON en la propia respuesta.',
    ],
    service: 'app/services/calendar_guard_service.py',
    integrations: ['Hostify', 'Cosmos DB', 'Telegram'],
    params: [
      { label: 'Autenticación', value: 'function key (auth_level=FUNCTION)' },
      { label: 'listing', value: 'acota por nickname o ID de Hostify', note: 'opcional' },
      { label: 'dry_run', value: '1 para solo informar de lo que cerraría', note: 'opcional' },
    ],
    whenOff: 'No se puede lanzar el guard a mano: responde {"status":"disabled"} sin hacer nada. El timer de las 04:30 tiene su propio interruptor.',
  },
  {
    id: 'alerts_telegram_http',
    name: 'Relay de alertas a Telegram',
    category: 'operacion',
    kind: 'http',
    endpoint: 'POST /api/alerts/telegram',
    when: 'HTTP',
    sortKey: 5025,
    summary: 'Publica en el topic de Sistemas las alertas de Azure Monitor: los fallos de este cron y también los de la web.',
    detail: [
      'Lo llama el action group sereno-alertas-criticas. Azure Monitor publica un JSON que la Bot API de Telegram no entiende, así que este endpoint lo traduce a un mensaje con la regla, la gravedad, cuántas coincidencias hubo, la hora de Madrid y el enlace a Application Insights.',
      'Ese action group lo comparten las reglas de tusereno-es-web, así que por aquí pasan también, entre otras, la reserva no creada (Sev0), la firma del webhook de Stripe (Sev1), los 5xx de la web y de checkin y el límite de Hostify.',
      'Hasta el 5 de octubre de 2026 iban al topic de Incidencias, donde el equipo da de alta los problemas de las casas, y se mezclaban con ellos. Ahora van al de Sistemas.',
      'Las reglas vigilan las trazas de error, no las ejecuciones fallidas: cada trigger captura su excepción, así que para Azure la ejecución acaba bien. La alerta principal salta con los errores de cabecera ([TIMER_… / [HTTP_…), que significan que la automatización abortó entera; los errores internos solo avisan en brote (más de 5 en 15 minutos). Una tercera regla avisa si en 3 horas no se ejecuta ninguna función.',
      'No usa _get_services(): una alerta provocada justo por Cosmos caído moriría al construir el aviso.',
      'Responde 200 aunque falle el envío: un 5xx haría que Azure reintentara y duplicase el mensaje.',
    ],
    service: 'function_app.py',
    integrations: ['Azure Monitor', 'Application Insights', 'Telegram'],
    params: [
      { label: 'Autenticación', value: 'function key en la URL del action group' },
      { label: 'Formato de entrada', value: 'common alert schema' },
      { label: 'Destino', value: 'telegram-topic-sistemas', note: 'si falta, al chat general del grupo' },
    ],
    notifies: 'Telegram · topic Sistemas',
    whenOff: 'Dejan de llegar a Telegram todas las alertas de Azure Monitor: las de este cron y las de la web, incluida la de reserva no creada. Siguen llegando por correo a gestion@tusereno.es.',
  },
  {
    id: 'health_cosmos_http',
    name: 'Health de Cosmos',
    category: 'operacion',
    kind: 'http',
    endpoint: 'GET /api/health/cosmos',
    when: 'HTTP',
    sortKey: 5030,
    summary: 'Comprueba que la Function App sigue viva y que la conexión a Cosmos por AAD funciona.',
    detail: [
      'Existe porque todas las demás funciones son por temporizador: sin este endpoint, tras un despliegue había que esperar horas a que saltara un timer para saber si la app seguía en pie.',
      'Devuelve solo un recuento de listings, sin datos: 200 con {"status":"ok","auth":"aad"} o 503 con el error.',
      'El workflow de despliegue lo consulta tras publicar y da el despliegue por fallido si no responde 200 en cinco minutos.',
    ],
    service: 'function_app.py',
    integrations: ['Cosmos DB'],
    params: [
      { label: 'Autenticación', value: 'anónima' },
      { label: 'Respuestas', value: '200 ok / 503 error' },
    ],
    noSwitchReason: 'No hace nada que se pueda querer parar, y el despliegue lo usa para comprobar que la app responde: apagarlo solo haría fallar los despliegues.',
  },
];
